import React, { useState, useEffect } from 'react';
import { useErrorReport } from '../../hooks/useErrorReport';
import { BaseModal } from '../ui/modal';
import NotificationModal from '../common/NotificationModal';

interface ErrorReportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ErrorType = 'bug' | 'feature' | 'ui' | 'performance' | 'other';

const ErrorReportModal: React.FC<ErrorReportModalProps> = ({ isOpen, onClose }) => {
  const { createErrorReport, sendErrorReport } = useErrorReport();
  
  const [step, setStep] = useState<'form' | 'sending' | 'success' | 'error'>('form');
  const [formData, setFormData] = useState({
    description: '',
    errorType: 'bug' as ErrorType,
    reproductionSteps: '',
    expectedBehavior: '',
    actualBehavior: ''
  });
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [notification, setNotification] = useState<{
    isOpen: boolean;
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: ''
  });

  const showNotification = (type: 'success' | 'error' | 'warning' | 'info', title: string, message: string) => {
    setNotification({
      isOpen: true,
      type,
      title,
      message
    });
  };

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('form');
      setFormData({
        description: '',
        errorType: 'bug',
        reproductionSteps: '',
        expectedBehavior: '',
        actualBehavior: ''
      });
      setAttachedFile(null);
    }
  }, [isOpen]);

  const errorTypeOptions = [
    { value: 'bug', label: 'Error/Bug' },
    { value: 'ui', label: 'Visual' },
    { value: 'performance', label: 'Lentitud' },
    { value: 'feature', label: 'Sugerencia' },
    { value: 'other', label: 'Otro' }
  ];


  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar que sea imagen
      if (file.type.startsWith('image/')) {
        setAttachedFile(file);
      } else {
        showNotification('warning', 'Archivo no válido', 'Por favor selecciona solo archivos de imagen (PNG, JPG, etc.)');
      }
    }
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      showNotification('warning', 'Descripción requerida', 'Por favor describe el problema antes de enviarlo.');
      return;
    }

    try {
      setStep('sending');
      
      let screenshotData = null;
      
      // Convertir archivo adjunto si existe
      if (attachedFile) {
        screenshotData = await convertFileToBase64(attachedFile);
      }

      // Crear reporte con toda la información
      const reportData = await createErrorReport(
        formData.description,
        formData.errorType,
        'medium', // Default severity
        {
          reproductionSteps: formData.reproductionSteps || undefined,
          expectedBehavior: formData.expectedBehavior || undefined,
          actualBehavior: formData.actualBehavior || undefined
        }
      );

      // Añadir screenshot si se subió archivo
      if (screenshotData) {
        reportData.screenshot = screenshotData;
      }

      // Enviar reporte
      const success = await sendErrorReport(reportData);
      
      if (success) {
        setStep('success');
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setStep('error');
      }
    } catch (error) {
      console.error('Error enviando reporte:', error);
      setStep('error');
    }
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} size="2xl" showCloseButton={false}>
      <div className="-m-6">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-lg">
              <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Reportar Problema
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                ¡Ayúdanos a mejorar GuideMe!
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        {step === 'form' && (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            
            {/* Tipo de error - Compacto */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tipo de problema
              </label>
              <div className="flex flex-wrap gap-2">
                {errorTypeOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, errorType: option.value as ErrorType }))}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                      formData.errorType === option.value
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>


            {/* Descripción */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Describe el problema *
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                required
                placeholder="Explica qué estaba pasando cuando encontraste el problema..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              />
            </div>

            {/* Detalles adicionales */}
            <details className="group">
              <summary className="flex items-center justify-between cursor-pointer list-none text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
                <span>Añadir más detalles (opcional)</span>
                <svg className="w-5 h-5 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="mt-4 space-y-4">
                <div>
                  <label htmlFor="reproductionSteps" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Pasos para reproducir
                  </label>
                  <textarea
                    id="reproductionSteps"
                    value={formData.reproductionSteps}
                    onChange={(e) => setFormData(prev => ({ ...prev, reproductionSteps: e.target.value }))}
                    rows={3}
                    placeholder="1. Hice clic en...&#10;2. Luego fui a...&#10;3. Entonces pasó..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  />
                </div>
                <div>
                  <label htmlFor="expectedBehavior" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    ¿Qué esperabas que pasara?
                  </label>
                  <textarea
                    id="expectedBehavior"
                    value={formData.expectedBehavior}
                    onChange={(e) => setFormData(prev => ({ ...prev, expectedBehavior: e.target.value }))}
                    rows={2}
                    placeholder="Esperaba que..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  />
                </div>
                <div>
                  <label htmlFor="actualBehavior" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    ¿Qué pasó realmente?
                  </label>
                  <textarea
                    id="actualBehavior"
                    value={formData.actualBehavior}
                    onChange={(e) => setFormData(prev => ({ ...prev, actualBehavior: e.target.value }))}
                    rows={2}
                    placeholder="Pero en su lugar..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  />
                </div>
              </div>
            </details>

            {/* Adjuntar archivo */}
            <div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Adjuntar imagen (opcional)
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Recomendado para problemas visuales
                  </span>
                </div>
                
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="block w-full text-sm text-gray-500 dark:text-gray-400
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-medium
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100
                    dark:file:bg-blue-900/20 dark:file:text-blue-400
                    dark:hover:file:bg-blue-900/30"
                />
                
                {attachedFile && (
                  <div className="mt-2 text-sm text-green-600 dark:text-green-400">
                    Archivo seleccionado: {attachedFile.name}
                  </div>
                )}
                
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Formatos soportados: PNG, JPG, JPEG, GIF, WEBP
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium"
              >
                Enviar Reporte
              </button>
            </div>
          </form>
        )}

        {/* Sending */}
        {step === 'sending' && (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Enviando reporte...
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              Recopilando información y enviando por email
            </p>
          </div>
        )}

        {/* Success */}
        {step === 'success' && (
          <div className="p-12 text-center">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              ¡Reporte enviado!
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              Gracias por ayudarnos a mejorar GuideMe
            </p>
          </div>
        )}

        {/* Error */}
        {step === 'error' && (
          <div className="p-12 text-center">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Error al enviar
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              No se pudo enviar el reporte. Inténtalo de nuevo.
            </p>
            <div className="flex items-center justify-center space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Cerrar
              </button>
              <button
                onClick={() => setStep('form')}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium"
              >
                Reintentar
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={() => setNotification({ ...notification, isOpen: false })}
        type={notification.type}
        title={notification.title}
        message={notification.message}
      />
    </BaseModal>
  );
};

export default ErrorReportModal;